import React, { useEffect, useRef } from 'react';

import styled from 'styled-components';

import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import SendIcon from '@grnhse/seedling/lib/birch/icons/send';
import Tokens from '@grnhse/seedling/lib/birch/tokens';

import { withLocalePrefix } from 'shared/utils/translation';
import { chatMessageTextStyles } from './message_styles';

const t = withLocalePrefix('shared.agent_chat');

// A size="md" IconButton is a 24px icon plus 4px of content-box padding.
const SEND_BUTTON_SIZE = '2rem';

type Props = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  placeholder?: string;
  sendLabel?: string;
  /** Drops the divider and the field's own border for callers that supply their own frame. */
  bare?: boolean;
};

export function MessageInput({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder = t('placeholder'),
  sendLabel = t('send'),
  bare = false,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [value]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!disabled) {
        onSubmit();
      }
    }
  };

  const Composer = bare ? BareComposer : FramedComposer;

  return (
    <Composer>
      <PromptRow>
        <Prompt>
          <StyledTextarea
            ref={textareaRef}
            value={value}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              onChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
          />
        </Prompt>
        <SubmitButton>
          <IconButton
            focusable
            icon={SendIcon}
            label={sendLabel}
            onClick={onSubmit}
            size="md"
            disabled={disabled}
            flat
          />
        </SubmitButton>
      </PromptRow>
    </Composer>
  );
}

const PromptRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${Tokens.spacing['1']};
`;

// min-height matches the send button so the two centre on each other on a single line,
// while the row's flex-end keeps the button on the last line as the prompt grows.
const Prompt = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  min-height: ${SEND_BUTTON_SIZE};
  background: ${FullPalette.WHITE};
`;

// flex, not block: IconButton is inline-flex, and a block parent wraps it in a line box that
// adds ~1.5px of strut descent below the button, knocking the row out of alignment.
const SubmitButton = styled.div`
  display: flex;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  resize: none;
  border: none;
  outline: none;
  padding: 0;
  overflow: hidden auto;
  max-height: 10.5rem;
  font-family: 'Untitled Sans', sans-serif;
  ${chatMessageTextStyles}

  &::placeholder {
    color: ${FullPalette.GREY_800};
  }
`;

const BareComposer = styled.div`
  padding: ${Tokens.spacing['1']} ${Tokens.spacing['2']};
`;

// Draws the field border and the divider above it for callers with no frame of their own. The
// button's padding offsets that border and padding, which the bare prompt doesn't have.
const FramedComposer = styled.div`
  padding: ${Tokens.spacing['2']} ${Tokens.spacing['3']};
  border-top: 1px solid ${FullPalette.GREY_700};

  ${Prompt} {
    min-height: 0;
    border: 1px solid ${FullPalette.GREY_700};
    border-radius: ${Tokens.spacing['0.5']};
    padding: ${Tokens.spacing['1']} ${Tokens.spacing['2']};
  }

  ${SubmitButton} {
    padding: ${Tokens.spacing['0.5']} 0;
  }
`;
